# Partner tokens

Partner tokens authenticate your software’s backend to the SEEK API.

A partner token grants access to the data of any SEEK hirer you have a [relationship] with.
Partner tokens must never be sent to a third-party system such as a hirer’s browser.

## Requesting a partner token

Exchange your [client credentials] for a partner token using `auth.seek.com`.
This flow implements the [OAuth 2.0 client credentials grant].

```mermaid Partner token exchange
sequenceDiagram
  autonumber
  participant Backend as Partner backend
  participant Auth as auth.seek.com
  participant GraphQL as graphql.seek.com

  Backend->>Auth: Send client credentials
  Auth-->>Backend: Issue partner token
  Backend->>GraphQL: Make requests to the GraphQL endpoint
```

1. Call the partner authentication endpoint with your client credentials.

   ```http label="Request"
   POST https://auth.seek.com/oauth/token HTTP/2
   Content-Type: application/json
   User-Agent: YourPartnerService/1.2.3

   {
     "audience": "https://graphql.seek.com",
     "client_id": "CLIENT_ID_HERE",
     "client_secret": "CLIENT_SECRET_HERE",
     "grant_type": "client_credentials"
   }
   ```

   The [Playground] environment uses a separate `https://test.graphql.seek.com` audience.

2. Receive the issued partner token.

   ```http label="Response"
   HTTP/2 200 OK
   Content-Type: application/json

   {
     "access_token": "PARTNER_TOKEN_HERE",
     "expires_in": 1800,
     "token_type": "Bearer"
   }
   ```

   Partner tokens must be cached for the number of seconds specified in the response’s `expires_in`.
   The cache expiry must be read from each response;
   it cannot be hardcoded as the token lifetime is dynamic and may be updated without notice.
   Caching reduces the load on SEEK’s systems and improves your software’s response time.

3. Pass the partner token in the HTTP `Authorization` header when making requests to the GraphQL endpoint.

   ```http label="Request"
   POST https://graphql.seek.com/graphql HTTP/2
   Accept-Language: en-AU
   Authorization: Bearer PARTNER_TOKEN_HERE
   User-Agent: YourPartnerService/1.2.3
   ```

## Token expiration

Re-initiate the client credentials flow in the above section to obtain a new partner token.
Note that this flow [does not feature a refresh token].

Using a partner token right up to its expiry may lead to expiration occurring mid-flight due to clock drift or request latency.
Leave a reasonable buffer of around a minute, or obtain a new partner token and retry the request on an [`UNAUTHENTICATED` error].

[`unauthenticated` error]: /graphql/error-responses#unauthenticated
[client credentials]: /auth#client-credentials
[does not feature a refresh token]: https://datatracker.ietf.org/doc/html/rfc6749#section-4.4.3
[oauth 2.0 client credentials grant]: https://datatracker.ietf.org/doc/html/rfc6749#section-4.4
[playground]: /graphql/playground
[relationship]: /auth/hirer-relationships
